<script>
  import { onMount } from 'svelte';
  import { router } from '@inertiajs/svelte';
  
  let challengeUrl = '/two_factor_authentication/challenge/security_keys/new';
  let verificationUrl = '/two_factor_authentication/challenge/security_keys';
  let fallbackUrl = '/two_factor_authentication/challenge/totp/new';
  
  let error = $state('');
  let isLoading = $state(false);
  let isSupported = $state(true);
  
  onMount(async () => {
    // Check if WebAuthn is supported in this browser
    try {
      if (!window.PublicKeyCredential) {
        isSupported = false;
        handleUnsupportedBrowser();
      }
    } catch (e) {
      isSupported = false;
      handleUnsupportedBrowser();
    }
  });
  
  function handleUnsupportedBrowser() {
    if (fallbackUrl) {
      router.visit(fallbackUrl);
    }
  }
  
  async function useSecurityKey() {
    try {
      error = '';
      isLoading = true;
      
      // Request the challenge from the server
      const challengeResponse = await fetch(challengeUrl, {
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (!challengeResponse.ok) {
        throw new Error('Failed to get authentication challenge');
      }
      
      const publicKeyOptions = await challengeResponse.json();
      
      // Prepare the PublicKeyCredentialRequestOptions
      const publicKey = {
        challenge: base64URLToBuffer(publicKeyOptions.challenge),
        allowCredentials: publicKeyOptions.allowCredentials.map(credential => ({
          id: base64URLToBuffer(credential.id),
          type: credential.type,
          transports: credential.transports
        })),
        timeout: publicKeyOptions.timeout,
        userVerification: publicKeyOptions.userVerification,
        rpId: publicKeyOptions.rpId
      };
      
      // Get the credential
      const credential = await navigator.credentials.get({ publicKey });
      
      // Format the credential for the server
      const credentialForServer = {
        id: credential.id,
        rawId: bufferToBase64URL(credential.rawId),
        type: credential.type,
        response: {
          authenticatorData: bufferToBase64URL(credential.response.authenticatorData),
          clientDataJSON: bufferToBase64URL(credential.response.clientDataJSON),
          signature: bufferToBase64URL(credential.response.signature),
          userHandle: credential.response.userHandle ? bufferToBase64URL(credential.response.userHandle) : null
        }
      };
      
      // Send the credential to the server for verification
      const verificationResponse = await fetch(verificationUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ credential: credentialForServer })
      });
      
      if (!verificationResponse.ok) {
        const errorData = await verificationResponse.json();
        throw new Error(errorData.error || 'Verification failed');
      }
      
      const result = await verificationResponse.json();
      if (result.location) {
        window.location.href = result.location;
      }
      
    } catch (e) {
      if (e.name === 'NotAllowedError') {
        error = "That didn't work. Either it was cancelled or took too long. Please try again.";
      } else if (e.name === 'InvalidStateError') {
        error = "We couldn't validate that security key. Please try again.";
      } else {
        error = e.message || 'An error occurred during authentication';
      }
    } finally {
      isLoading = false;
    }
  }
  
  // Utility functions for buffer/base64URL conversion
  function bufferToBase64URL(buffer) {
    const bytes = new Uint8Array(buffer);
    let str = '';
    for (const byte of bytes) {
      str += String.fromCharCode(byte);
    }
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  
  function base64URLToBuffer(base64URL) {
    const base64 = base64URL.replace(/-/g, '+').replace(/_/g, '/');
    const padLength = (4 - (base64.length % 4)) % 4;
    const padded = base64 + '='.repeat(padLength);
    const binary = atob(padded);
    const buffer = new ArrayBuffer(binary.length);
    const bytes = new Uint8Array(buffer);
    
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    
    return buffer;
  }
</script>

<div class="security-key-container" class:is-loading={isLoading}>
  {#if error}
    <p class="error">{error}</p>
  {/if}

  <h1>Verify with your security key.</h1>
  
  {#if isSupported}
    <button 
      type="button" 
      class="security-key-button" 
      onclick={useSecurityKey} 
      disabled={isLoading}
    >
      {isLoading ? 'Processing...' : 'Use security key'}
    </button>
    
    <p class="instruction">Have your security key ready. If it's the USB kind insert it now and then, if it has one, press the activation button when asked.</p>
  {/if}
</div>

<style>
  .security-key-container {
    max-width: 600px;
    margin: 0 auto;
    padding: 2rem 1rem;
  }
  
  .security-key-container.is-loading {
    opacity: 0.7;
    pointer-events: none;
  }
  
  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
  }
  
  .error {
    color: #b91c1c;
    margin-bottom: 1rem;
    padding: 0.5rem;
    background-color: #fee2e2;
    border-radius: 0.375rem;
  }
  
  .security-key-button {
    background-color: #047857;
    color: white;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 0.375rem;
    font-weight: 500;
    font-size: 1rem;
    cursor: pointer;
    margin-bottom: 1rem;
  }
  
  .security-key-button:hover:not(:disabled) {
    background-color: #065f46;
  }
  
  .security-key-button:disabled {
    background-color: #93c5fd;
    cursor: not-allowed;
  }
  
  .instruction {
    font-size: 0.9rem;
    color: #4b5563;
    line-height: 1.5;
  }
</style>